import { useCallback, useRef, useEffect } from 'react';
import api from '../services/api';

const SYNC_KEYS = [
  'gym_notebook_walking_session_history',
  'gym_notebook_walking_daily_data',
  'gym_notebook_walking_routines',
  'gym_notebook_walking_stride_length',
  'gym_notebook_meal_times',
  'gym_notebook_nutrition_goals',
  'gym_notebook_waist_history',
  'gym_notebook_body_measurements_history',
  'gym_notebook_persistent_bodyweight',
  'gym_notebook_persistent_weight_unit',
  'gym_notebook_persistent_height',
  'gym_notebook_persistent_height_unit',
  'gym_notebook_persistent_age',
  'gym_notebook_persistent_gender',
  'gym_notebook_persistent_measurements',
  'gym_notebook_waist',
  'gym_notebook_waist_unit',
  'gym_notebook_weight_goal',
  'gym_notebook_weight_goal_start',
  'medications_enabled',
  'medications_notifications_desired',
  'diabetes_enabled',
  'diabetes_unit',
  'diabetes_insulin',
  'diabetes_type',
  'health_conditions',
  'walking_step_goal_settings',
  'walking_daily_steps',
];

const DEBOUNCE_MS = 5000;
const SYNC_INTERVAL_MS = 60000;

// Patch localStorage.setItem once to dispatch events for same-tab detection
const _origSetItem = localStorage.setItem.bind(localStorage);
let _patched = false;
function patchLocalStorage() {
  if (_patched) return;
  _patched = true;
  localStorage.setItem = function (key, value) {
    _origSetItem(key, value);
    window.dispatchEvent(new CustomEvent('local-storage-set', { detail: { key } }));
  };
}

function collectLocalData() {
  const data = {};
  for (const key of SYNC_KEYS) {
    const val = localStorage.getItem(key);
    if (val !== null) data[key] = val;
  }
  return data;
}

function collectNutritionData() {
  const nutrition = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('nutrition_') && key.length <= 25) {
      nutrition[key] = localStorage.getItem(key);
    }
  }
  return nutrition;
}

export default function useCloudSync(isAuthenticated) {
  const debounceTimer = useRef(null);
  const isSyncing = useRef(false);

  // Patch localStorage.setItem on first mount
  useEffect(() => { patchLocalStorage(); }, []);

  // Pull from cloud → merge into localStorage
  const pullFromCloud = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await api.get('/sync/cloud');
      if (!result) return;
      const { data, nutrition } = result;

      // Cloud always wins — it has the latest data from any device
      if (data) {
        for (const [key, val] of Object.entries(data)) {
          _origSetItem(key, val);
        }
        // Notify React components to re-sync from localStorage
        window.dispatchEvent(new Event('cloud-sync-pulled'));
      }

      if (nutrition) {
        for (const [key, val] of Object.entries(nutrition)) {
          _origSetItem(key, val);
        }
      }
    } catch (e) {
      // Silent fail — sync is best-effort
    }
  }, [isAuthenticated]);

  // Push localStorage → cloud
  const pushToCloud = useCallback(async () => {
    if (!isAuthenticated || isSyncing.current) return;
    isSyncing.current = true;
    try {
      const data = collectLocalData();
      const nutrition = collectNutritionData();
      await api.post('/sync/cloud', { data, nutrition });
    } catch (e) {
      // Silent fail
    } finally {
      isSyncing.current = false;
    }
  }, [isAuthenticated]);

  // Debounced push — call this whenever data changes
  const schedulePush = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(pushToCloud, DEBOUNCE_MS);
  }, [pushToCloud]);

  // On login: pull from cloud, then push local data to fill any gaps
  useEffect(() => {
    if (!isAuthenticated) return;
    let mounted = true;
    const init = async () => {
      await pullFromCloud();
      if (mounted) {
        // Small delay then push local data to cloud
        setTimeout(pushToCloud, 2000);
      }
    };
    init();
    return () => { mounted = false; };
  }, [isAuthenticated, pullFromCloud, pushToCloud]);

  // Periodic auto-sync
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(pushToCloud, SYNC_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, pushToCloud]);

  // Listen for localStorage changes (same-tab via custom event, cross-tab via storage event)
  useEffect(() => {
    const shouldSync = (key) => SYNC_KEYS.includes(key) || key?.startsWith('nutrition_');
    const handleSet = (e) => {
      if (shouldSync(e.detail?.key)) schedulePush();
    };
    const handleStorage = (e) => {
      if (shouldSync(e.key)) schedulePush();
    };
    window.addEventListener('local-storage-set', handleSet);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('local-storage-set', handleSet);
      window.removeEventListener('storage', handleStorage);
    };
  }, [schedulePush]);

  return { pushToCloud, pullFromCloud, schedulePush };
}
